const node = (tag, className, text) => {
  const result = document.createElement(tag);
  if (className) result.className = className;
  if (text !== undefined) result.textContent = text;
  return result;
};

// These fixed illustrations are independent of the customer's uploaded artwork.
const paths = {
  size: '<path d="M6 7h12l-1.7 13H7.7L6 7Z"/><path d="M5 4h14v3H5zM9 11h6M9.5 14h5"/>',
  none: '<circle cx="12" cy="12" r="8"/><path d="m6.3 6.3 11.4 11.4"/>',
  press: '<path d="M4 12h16l-1 4H5l-1-4ZM6 12l1-5h10l1 5M8 7V5h8v2M9 10h6M4 19h16"/>',
  sip: '<path d="M4 12h16l-1 4H5l-1-4ZM6 12l1-5h10l1 5M13 7V4h3v3M8 10h3M4 19h16"/>',
  color: '<circle cx="12" cy="12" r="8"/><path d="M12 4a8 8 0 0 1 0 16V4Z" fill="currentColor"/>',
  chevron: '<path d="m6 9 6 6 6-6"/>',
  check: '<path d="m6 12 4 4 8-8"/>',
};

function icon(name) {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.6');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  svg.innerHTML = paths[name] || paths.color;
  return svg;
}

export class ProductOptionPicker {
  constructor({ select, kind, id, listen, onOpen }) {
    this.select = select;
    this.kind = kind;
    this.onOpen = onOpen;
    this.id = `CustomChoice-${id}-${kind}`;
    this.label = select.getAttribute('aria-label');
    this.host = node('div', `ucp-picker ucp-picker--${kind}`);
    this.host.dataset.ucpPicker = kind;
    const fallback = select.closest('.ucp-select-row');
    fallback.before(this.host);
    this.host.append(fallback);

    this.button = node('button', 'ucp-picker__trigger');
    this.button.type = 'button';
    this.button.setAttribute('role', 'combobox');
    this.button.setAttribute('aria-haspopup', 'listbox');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.setAttribute('aria-controls', `${this.id}-list`);
    this.button.setAttribute('aria-labelledby', `${this.id}-label ${this.id}-value`);
    const label = node('span', 'ucp-picker__label', this.label);
    label.id = `${this.id}-label`;
    this.value = node('span', 'ucp-picker__value');
    this.value.id = `${this.id}-value`;
    const arrow = node('span', 'ucp-picker__chevron');
    arrow.append(icon('chevron'));
    this.button.append(label, this.value, arrow);

    this.popup = node('div', 'ucp-picker__popup');
    this.popup.hidden = true;
    const heading = node('div', 'ucp-picker__heading');
    heading.append(node('strong', '', `Choose ${this.label.toLowerCase()}`));
    this.count = node('span'); heading.append(this.count);
    this.list = node('div', 'ucp-picker__list');
    this.list.id = `${this.id}-list`;
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-label', `${this.label} options`);
    this.popup.append(heading, this.list);
    this.host.append(this.button, this.popup);
    // The native fields remain the source for the existing pricing and order flow.
    fallback.hidden = true;

    listen(this.button, 'click', () => this.isOpen ? this.close() : this.open());
    listen(this.button, 'keydown', (event) => this.keydown(event));
    // Keep combobox focus while choosing an option; focusout must not hide it before click.
    listen(this.list, 'pointerdown', (event) => event.preventDefault());
    listen(this.list, 'click', (event) => {
      const option = event.target.closest('[data-picker-index]');
      if (option) this.commit(Number(option.dataset.pickerIndex));
    });
    listen(this.list, 'pointermove', (event) => {
      if (event.pointerType === 'touch') return;
      const option = event.target.closest('[data-picker-index]');
      if (option) this.activate(Number(option.dataset.pickerIndex), false);
    });
    listen(this.host, 'focusout', (event) => {
      if (!this.host.contains(event.relatedTarget)) this.close();
    });
  }

  details(entry) {
    if (this.kind === 'size') {
      const capacity = (entry.capacity || entry.label).split(/\s*[|/]\s*/);
      return { title:capacity[0], detail:[capacity[1], entry.rim ? `${entry.rim} mm rim` : ''].filter(Boolean).join(' · ') };
    }
    if (this.kind === 'lid') return { title:entry.label, detail:entry.description || '' };
    return { title:entry.label, detail:'' };
  }

  visual(entry) {
    const visual = node('span', 'ucp-picker__visual');
    visual.setAttribute('aria-hidden', 'true');
    if (this.kind === 'color' && entry.id !== 'none') {
      const swatch = node('span', 'ucp-picker__swatch');
      swatch.style.backgroundColor = { white:'#fff', black:'#252a27' }[entry.id] || '#d8e2db';
      visual.append(swatch);
    } else visual.append(icon(this.kind === 'size' ? 'size' : this.kind === 'lid' ? entry.id : 'color'));
    return visual;
  }

  sync(entries, value) {
    this.close();
    this.entries = entries.length ? entries : [{ id:'none', label:'Not applicable' }];
    this.selected = Math.max(0, this.entries.findIndex(entry => entry.id === value));
    this.count.textContent = `${this.entries.length} ${this.kind === 'size' ? 'sizes' : this.kind === 'color' ? 'colors' : 'options'}`;
    this.list.replaceChildren(...this.entries.map((entry, index) => {
      const option = node('div', 'ucp-picker__option');
      option.id = `${this.id}-option-${index}`;
      option.dataset.pickerIndex = String(index);
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', String(index === this.selected));
      const { title, detail } = this.details(entry);
      const copy = node('span', 'ucp-picker__copy');
      copy.append(node('strong', '', title));
      if (detail) copy.append(node('small', '', detail));
      const check = node('span', 'ucp-picker__check'); check.append(icon('check'));
      option.append(this.visual(entry), copy, check);
      return option;
    }));
    const selected = this.entries[this.selected];
    const { title, detail } = this.details(selected);
    const copy = node('span', 'ucp-picker__copy');
    copy.append(node('strong', '', title));
    if (detail) copy.append(node('small', '', detail));
    this.value.replaceChildren(this.visual(selected), copy);
    this.setDisabled(this.select.disabled);
  }

  setDisabled(disabled) {
    this.button.disabled = disabled;
    if (disabled) this.close();
    if (this.kind === 'color' && this.entries?.[this.selected]?.id === 'none') {
      const copy = node('span', 'ucp-picker__copy');
      copy.append(node('strong', '', 'Choose a lid first'), node('small', '', 'Color available with a lid'));
      this.value.replaceChildren(this.visual({id:'none'}), copy);
    }
  }

  open() {
    if (this.button.disabled || !this.entries?.length) return;
    this.onOpen(this);
    const rect = this.button.getBoundingClientRect();
    const viewport = window.visualViewport;
    const viewportTop = viewport?.offsetTop || 0;
    const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
    const headerBottom = document.querySelector('.section-header')?.getBoundingClientRect().bottom || 0;
    const top = Math.max(viewportTop + 12, headerBottom > viewportTop && headerBottom < rect.top ? headerBottom + 12 : 0);
    const below = viewportBottom - rect.bottom - 20;
    const above = rect.top - top - 8;
    const side = below < 300 && above > below ? 'above' : 'below';
    this.host.dataset.side = side;
    this.popup.style.maxHeight = `${Math.max(120, Math.min(360, side === 'above' ? above : below))}px`;
    this.popup.hidden = false;
    this.host.classList.add('is-open');
    this.button.setAttribute('aria-expanded', 'true');
    this.activate(this.selected);
  }

  get isOpen() { return !this.popup.hidden; }

  close() {
    this.popup.hidden = true;
    this.host.classList.remove('is-open');
    this.button.setAttribute('aria-expanded', 'false');
    this.button.removeAttribute('aria-activedescendant');
  }

  activate(index, scroll = true) {
    this.active = index;
    const options = [...this.list.children];
    options.forEach((option, i) => option.classList.toggle('is-active', i === index));
    const option = options[index];
    if (!option) return;
    this.button.setAttribute('aria-activedescendant', option.id);
    if (scroll) {
      const item = option.getBoundingClientRect(), list = this.list.getBoundingClientRect();
      if (item.top < list.top) this.list.scrollTop -= list.top - item.top;
      else if (item.bottom > list.bottom) this.list.scrollTop += item.bottom - list.bottom;
    }
  }

  commit(index) {
    const entry = this.entries[index];
    if (!entry || this.button.disabled) return;
    this.close();
    this.select.value = entry.id;
    this.select.dispatchEvent(new Event('change', { bubbles:true }));
    this.button.focus({ preventScroll:true });
  }

  keydown(event) {
    const key = event.key;
    if (key === 'Tab' || key === 'Escape') {
      if (key === 'Escape' && this.isOpen) { event.preventDefault(); event.stopPropagation(); }
      this.close(); return;
    }
    if (['Enter', ' ', 'ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) {
      event.preventDefault();
      if (!this.isOpen) {
        this.open();
        if (key === 'Home') this.activate(0);
        if (key === 'End') this.activate(this.entries.length - 1);
        return;
      }
      if (key === 'Enter' || key === ' ') this.commit(this.active);
      else this.activate(key === 'Home' ? 0 : key === 'End' ? this.entries.length - 1 : (this.active + (key === 'ArrowDown' ? 1 : -1) + this.entries.length) % this.entries.length);
    } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      if (!this.isOpen) this.open();
      const previous = Date.now() - (this.typedAt || 0) < 700 ? this.typed || '' : '';
      this.typed = previous + key.toLowerCase(); this.typedAt = Date.now();
      const search = [...this.typed].every(char => char === this.typed[0]) ? this.typed[0] : this.typed;
      const start = search.length === 1 ? this.active + 1 : this.active;
      for (let step = 0; step < this.entries.length; step++) {
        const index = (start + step) % this.entries.length;
        if (this.details(this.entries[index]).title.toLowerCase().startsWith(search)) { this.activate(index); break; }
      }
    }
  }
}
